// admin/src/pages/RecycleBin.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useRecycleBin } from '../context/RecycleBinContext';
import './RecycleBin.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://tripsie.in/api';

const fmtDate  = (d) => new Date(d).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });
const fmtPrice = (p) => p?.toLocaleString('en-IN') || '0';

export default function RecycleBin() {
  const [tab, setTab] = useState('bookings'); // 'bookings' | 'reviews'

  const [bookings,      setBookings]      = useState([]);
  const [reviews,       setReviews]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [emptyConfirm,  setEmptyConfirm]  = useState(false);
  const [working,       setWorking]       = useState(null);
  const [deleteTarget,     setDeleteTarget]     = useState(null); // { type: 'booking'|'review', item }
  const [restoreTarget,    setRestoreTarget]    = useState(null); // { type: 'booking'|'review', item }
  const [restoreReviewToo, setRestoreReviewToo] = useState(false);

  const { refreshCount } = useRecycleBin();

  const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}` });

  const fetchBin = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [bRes, rRes] = await Promise.all([
        fetch(`${API_BASE_URL}/bookings/admin/recycle-bin`, { headers: hdrs() }),
        fetch(`${API_BASE_URL}/reviews/admin/recycle-bin/all`, { headers: hdrs() }),
      ]);
      const [bData, rData] = await Promise.all([bRes.json(), rRes.json()]);
      if (bData.success) setBookings(bData.bookings);
      if (rData.success) setReviews(rData.reviews);
      if (!bData.success && !rData.success) setError(bData.message || rData.message || 'Failed to load.');
    } catch { setError('Cannot connect to server.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBin(); }, [fetchBin]);
  useEffect(() => { setEmptyConfirm(false); }, [tab]);

  const activeList  = tab === 'bookings' ? bookings : reviews;
  const restoreUrl  = (id) => tab === 'bookings' ? `${API_BASE_URL}/bookings/admin/${id}/restore`  : `${API_BASE_URL}/reviews/admin/${id}/restore`;
  const permUrl     = (id) => tab === 'bookings' ? `${API_BASE_URL}/bookings/admin/${id}/permanent` : `${API_BASE_URL}/reviews/admin/${id}/permanent`;
  const trashedField = tab === 'bookings' ? 'trashedAt' : 'deletedAt';
  const setActiveList = tab === 'bookings' ? setBookings : setReviews;

  // ── Restore ───────────────────────────────
  const confirmRestore = async () => {
    if (!restoreTarget) return;
    const id = restoreTarget.item._id;
    setWorking(id);
    try {
      const isBooking = restoreTarget.type === 'booking';
      const r = await fetch(restoreUrl(id), {
        method: 'PATCH',
        headers: { ...hdrs(), ...(isBooking ? { 'Content-Type': 'application/json' } : {}) },
        ...(isBooking ? { body: JSON.stringify({ restoreReview: restoreReviewToo }) } : {}),
      });
      const d = await r.json();
      if (d.success) {
        setActiveList((p) => p.filter((x) => x._id !== id));
        refreshCount();
        setRestoreTarget(null);
        setRestoreReviewToo(false);
      } else alert(d.message || 'Restore failed.');
    } catch { alert('Error restoring.'); }
    finally { setWorking(null); }
  };

  // ── Permanent delete ──────────────────────
  const permDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.item._id;
    setWorking(id);
    try {
      const r = await fetch(permUrl(id), { method: 'DELETE', headers: hdrs() });
      const d = await r.json();
      if (d.success) {
        setActiveList((p) => p.filter((x) => x._id !== id));
        refreshCount();
        setDeleteTarget(null);
      } else alert(d.message || 'Delete failed.');
    } catch { alert('Error deleting.'); }
    finally { setWorking(null); }
  };

  // ── Empty bin (current tab only) ──────────
  const emptyBin = async () => {
    setWorking('empty');
    try {
      if (tab === 'bookings') {
        const r = await fetch(`${API_BASE_URL}/bookings/admin/recycle-bin/empty`, { method: 'DELETE', headers: hdrs() });
        const d = await r.json();
        if (d.success) { setBookings([]); setEmptyConfirm(false); refreshCount(); }
        else alert(d.message || 'Failed to empty bin.');
      } else {
        // No bulk-empty endpoint for reviews — delete one by one.
        await Promise.all(reviews.map((rv) =>
          fetch(`${API_BASE_URL}/reviews/admin/${rv._id}/permanent`, { method: 'DELETE', headers: hdrs() })
        ));
        setReviews([]); setEmptyConfirm(false); refreshCount();
      }
    } catch { alert('Error emptying bin.'); }
    finally { setWorking(null); }
  };

  const daysLeft = (trashedAt) => {
    const expires = new Date(trashedAt).getTime() + 30 * 24 * 60 * 60 * 1000;
    const days    = Math.ceil((expires - Date.now()) / (24 * 60 * 60 * 1000));
    return Math.max(0, days);
  };

  const isDeleting  = deleteTarget  && working === deleteTarget.item._id;
  const isRestoring = restoreTarget && working === restoreTarget.item._id;

  return (
    <div className="rb">
      {/* Header */}
      <div className="rb__header">
        <div>
          <h1 className="rb__title">Recycle Bin</h1>
          <p className="rb__sub">Trashed items · auto-deleted after 30 days</p>
        </div>
        <div className="rb__header-actions">
          <button className="rb__refresh" onClick={fetchBin} title="Refresh">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
            </svg>
          </button>

          {activeList.length > 0 && !emptyConfirm && (
            <button className="rb__empty-btn" onClick={() => setEmptyConfirm(true)}>
              Empty {tab === 'bookings' ? 'Bookings' : 'Reviews'} ({activeList.length})
            </button>
          )}

          {emptyConfirm && (
            <div className="rb__empty-confirm">
              <span>Delete all {activeList.length} permanently?</span>
              <button className="rb__ec-cancel"  onClick={() => setEmptyConfirm(false)} disabled={working === 'empty'}>Cancel</button>
              <button className="rb__ec-confirm" onClick={emptyBin}                     disabled={working === 'empty'}>
                {working === 'empty' ? 'Deleting…' : 'Yes, Delete All'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="rb__tabs">
        <button className={`rb__tab ${tab === 'bookings' ? 'rb__tab--active' : ''}`} onClick={() => setTab('bookings')}>
          Bookings <span className="rb__tab-count">{bookings.length}</span>
        </button>
        <button className={`rb__tab ${tab === 'reviews' ? 'rb__tab--active' : ''}`} onClick={() => setTab('reviews')}>
          Reviews <span className="rb__tab-count">{reviews.length}</span>
        </button>
      </div>

      {error && (
        <div className="rb__error">
          {error} <button onClick={fetchBin}>Retry</button>
        </div>
      )}

      <div className="rb__card">
        {loading ? (
          <div className="rb__loading"><div className="rb__spinner"/><p>Loading recycle bin…</p></div>

        ) : activeList.length === 0 ? (
          <div className="rb__empty">
            <div className="rb__empty-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                <path d="M10 11v6"/><path d="M14 11v6"/>
                <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
              </svg>
            </div>
            <h3>Recycle Bin is empty</h3>
            <p>{tab === 'bookings' ? 'Bookings' : 'Reviews'} moved to the bin will appear here.</p>
          </div>

        ) : tab === 'bookings' ? (
          <>
            {/* Desktop table — Bookings */}
            <div className="rb__table-wrap">
              <table className="rb__table">
                <thead>
                  <tr>
                    <th>Booking ID</th><th>Customer</th><th>Trip</th><th>Amount</th>
                    <th>Trashed On</th><th>Auto-deletes</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => {
                    const days = daysLeft(b[trashedField]);
                    return (
                      <tr key={b._id} className="rb__row">
                        <td><span className="rb__id">{b.bookingId}</span></td>
                        <td>
                          <div className="rb__customer">
                            <div className="rb__avatar">{(b.contactInfo?.name || '?').charAt(0).toUpperCase()}</div>
                            <div>
                              <div className="rb__cname">{b.contactInfo?.name || '—'}</div>
                              <div className="rb__cphone">{b.contactInfo?.phone}</div>
                            </div>
                          </div>
                        </td>
                        <td><div className="rb__trip">{b.tripTitle}</div></td>
                        <td><span className="rb__amount">₹{fmtPrice(b.grandTotal)}</span></td>
                        <td><span className="rb__date">{fmtDate(b[trashedField])}</span></td>
                        <td>
                          <span className={`rb__expires ${days <= 3 ? 'rb__expires--urgent' : days <= 7 ? 'rb__expires--warn' : ''}`}>
                            {days === 0 ? 'Today' : `${days}d left`}
                          </span>
                        </td>
                        <td>
                          <div className="rb__actions">
                            <button className="rb__restore-btn" disabled={!!working} onClick={() => { setRestoreTarget({ type: 'booking', item: b }); setRestoreReviewToo(false); }}>Restore</button>
                            <button className="rb__del-btn" disabled={!!working} onClick={() => setDeleteTarget({ type: 'booking', item: b })} title="Permanently delete">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards — Bookings */}
            <div className="rb__mobile-list">
              {bookings.map((b) => {
                const days = daysLeft(b[trashedField]);
                return (
                  <div key={b._id} className="rb__mobile-card">
                    <div className="rb__mobile-top">
                      <span className="rb__id">{b.bookingId}</span>
                      <span className={`rb__expires ${days <= 3 ? 'rb__expires--urgent' : days <= 7 ? 'rb__expires--warn' : ''}`}>
                        {days === 0 ? 'Today' : `${days}d left`}
                      </span>
                    </div>
                    <div className="rb__mobile-name">{b.contactInfo?.name || '—'}</div>
                    <div className="rb__mobile-trip">{b.tripTitle}</div>
                    <div className="rb__mobile-meta">
                      <span>₹{fmtPrice(b.grandTotal)}</span>
                      <span>Trashed {fmtDate(b[trashedField])}</span>
                    </div>
                    <div className="rb__actions rb__actions--mobile">
                      <button className="rb__restore-btn" disabled={!!working} onClick={() => { setRestoreTarget({ type: 'booking', item: b }); setRestoreReviewToo(false); }}>Restore</button>
                      <button className="rb__del-btn" disabled={!!working} onClick={() => setDeleteTarget({ type: 'booking', item: b })}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="rb__footer">{bookings.length} item{bookings.length !== 1 ? 's' : ''} in bin</div>
          </>
        ) : (
          <>
            {/* Desktop table — Reviews */}
            <div className="rb__table-wrap">
              <table className="rb__table">
                <thead>
                  <tr>
                    <th>Traveller</th><th>Trip</th><th>Rating</th><th>Review</th>
                    <th>Trashed On</th><th>Auto-deletes</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.map((rv) => {
                    const days = daysLeft(rv[trashedField]);
                    return (
                      <tr key={rv._id} className="rb__row">
                        <td>
                          <div className="rb__customer">
                            <div className="rb__avatar">{(rv.userName || 'T').charAt(0).toUpperCase()}</div>
                            <div className="rb__cname">{rv.userName || 'Traveller'}</div>
                          </div>
                        </td>
                        <td><div className="rb__trip">{rv.tripTitle || '—'}</div></td>
                        <td><span className="rb__amount">{rv.rating} ★</span></td>
                        <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {rv.text || <em>No written review</em>}
                        </td>
                        <td><span className="rb__date">{fmtDate(rv[trashedField])}</span></td>
                        <td>
                          <span className={`rb__expires ${days <= 3 ? 'rb__expires--urgent' : days <= 7 ? 'rb__expires--warn' : ''}`}>
                            {days === 0 ? 'Today' : `${days}d left`}
                          </span>
                        </td>
                        <td>
                          <div className="rb__actions">
                            <button className="rb__restore-btn" disabled={!!working} onClick={() => setRestoreTarget({ type: 'review', item: rv })}>Restore</button>
                            <button className="rb__del-btn" disabled={!!working} onClick={() => setDeleteTarget({ type: 'review', item: rv })} title="Permanently delete">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards — Reviews */}
            <div className="rb__mobile-list">
              {reviews.map((rv) => {
                const days = daysLeft(rv[trashedField]);
                return (
                  <div key={rv._id} className="rb__mobile-card">
                    <div className="rb__mobile-top">
                      <span className="rb__id">{rv.userName || 'Traveller'}</span>
                      <span className={`rb__expires ${days <= 3 ? 'rb__expires--urgent' : days <= 7 ? 'rb__expires--warn' : ''}`}>
                        {days === 0 ? 'Today' : `${days}d left`}
                      </span>
                    </div>
                    <div className="rb__mobile-name">{rv.rating} ★</div>
                    <div className="rb__mobile-trip">{rv.tripTitle || '—'}</div>
                    <div className="rb__mobile-meta">
                      <span>Trashed {fmtDate(rv[trashedField])}</span>
                    </div>
                    <div className="rb__actions rb__actions--mobile">
                      <button className="rb__restore-btn" disabled={!!working} onClick={() => setRestoreTarget({ type: 'review', item: rv })}>Restore</button>
                      <button className="rb__del-btn" disabled={!!working} onClick={() => setDeleteTarget({ type: 'review', item: rv })}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="rb__footer">{reviews.length} item{reviews.length !== 1 ? 's' : ''} in bin</div>
          </>
        )}
      </div>

      {/* ── Permanent Delete Modal ── */}
      {deleteTarget && (
        <div className="rb__modal-overlay" onClick={() => { if (!isDeleting) setDeleteTarget(null); }}>
          <div className="rb__modal" onClick={e => e.stopPropagation()}>
            <div className="rb__modal-icon rb__modal-icon--delete">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                <line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
              </svg>
            </div>
            <h3 className="rb__modal-title">Permanently Delete?</h3>
            <p className="rb__modal-body-text">
              {deleteTarget.type === 'booking' ? (
                <>Are you sure you want to permanently delete booking{' '}
                <strong>{deleteTarget.item.bookingId}</strong> for{' '}
                <strong>{deleteTarget.item.contactInfo?.name || 'this customer'}</strong>?</>
              ) : (
                <>Are you sure you want to permanently delete this review from{' '}
                <strong>{deleteTarget.item.userName || 'this traveller'}</strong>?</>
              )}
            </p>
            <p className="rb__modal-warn">This action cannot be undone.</p>
            <div className="rb__modal-actions">
              <button className="rb__modal-cancel" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>Cancel</button>
              <button className="rb__modal-confirm rb__modal-confirm--delete" onClick={permDelete} disabled={isDeleting}>
                {isDeleting ? (<><span className="rb__modal-spinner"/>Deleting…</>) : (
                  <>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
                    </svg>
                    Delete Permanently
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Restore Modal ── */}
      {restoreTarget && (
        <div className="rb__modal-overlay" onClick={() => { if (!isRestoring) { setRestoreTarget(null); setRestoreReviewToo(false); } }}>
          <div className="rb__modal" onClick={e => e.stopPropagation()}>
            <div className="rb__modal-icon rb__modal-icon--restore">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/>
              </svg>
            </div>
            <h3 className="rb__modal-title">Restore {restoreTarget.type === 'booking' ? 'Booking' : 'Review'}?</h3>
            <p className="rb__modal-body-text">
              {restoreTarget.type === 'booking' ? (
                <>Booking <strong>{restoreTarget.item.bookingId}</strong> for{' '}
                <strong>{restoreTarget.item.contactInfo?.name || 'this customer'}</strong> will be moved back to active bookings.</>
              ) : (
                <>This review from <strong>{restoreTarget.item.userName || 'this traveller'}</strong> will move back to{' '}
                <strong>Pending</strong> for a fresh approval decision.</>
              )}
            </p>
            <p className="rb__modal-info">
              {restoreTarget.type === 'booking' ? 'The booking will be fully restored and visible again.' : 'An admin will need to approve it again before it goes live.'}
            </p>

            {restoreTarget.type === 'booking' && restoreTarget.item.hasReview && (
              <label className="rb__modal-checkbox">
                <input
                  type="checkbox"
                  checked={restoreReviewToo}
                  onChange={(e) => setRestoreReviewToo(e.target.checked)}
                />
                <span>
                  Also restore this booking's review
                  {restoreReviewToo && <em> — it will go back to Pending; an admin will need to approve it again before it goes live.</em>}
                </span>
              </label>
            )}
            <div className="rb__modal-actions">
              <button className="rb__modal-cancel" onClick={() => { setRestoreTarget(null); setRestoreReviewToo(false); }} disabled={isRestoring}>Cancel</button>
              <button className="rb__modal-confirm rb__modal-confirm--restore" onClick={confirmRestore} disabled={isRestoring}>
                {isRestoring ? (<><span className="rb__modal-spinner rb__modal-spinner--dark"/>Restoring…</>) : (
                  <>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/>
                    </svg>
                    Restore {restoreTarget.type === 'booking' ? 'Booking' : 'Review'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
